import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RvHookupIcon from "@mui/icons-material/RvHookup";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { AuditLog } from "../../../api/auditApi";
import PanZoomViewport from "../../../components/common/PanZoomViewport";
import { DriverHistoryTab, getDriverHistoryTabConfigs } from "../auditEntityRegistry";
import {
  AssignmentTimelineSegment,
  buildAssignmentTimelineSegments,
  formatTimelineDateTimeLabel,
  getAuditActorLabel,
  getTransitionAuditLog,
} from "../utils/driverAssignmentTimelineUtils";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AssignmentEntityLabel from "./AssignmentEntityLabel";
import AuditActionChip from "./AuditActionChip";
import StateBoxPopover, { InfoRow, formatPopoverDateTime } from "./StateBoxPopover";

const TAB_ICONS: Record<DriverHistoryTab, React.ReactElement> = {
  vehicle: <LocalShippingIcon sx={{ fontSize: 20 }} />,
  trailer: <RvHookupIcon sx={{ fontSize: 20 }} />,
  dispatcher: <SupportAgentIcon sx={{ fontSize: 20 }} />,
};

interface DriverAssignmentTimelineProps {
  auditLogs: AuditLog[];
  visibleTabs: DriverHistoryTab[];
  isLoading?: boolean;
  open?: boolean;
  topLeftOverlay?: React.ReactNode;
  topRightOverlay?: React.ReactNode;
}

const SEGMENT_WIDTH = 120;
const TRANSITION_WIDTH = 160;
const STATE_BOX_HEIGHT = 64;
const LANE_HEIGHT = STATE_BOX_HEIGHT + 16;
const LANE_GAP = 24;
const LANE_LABEL_WIDTH = 100;

const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      border: 1,
      borderColor: "divider",
      borderRadius: 1.5,
      boxShadow: 4,
      color: "text.primary",
      maxWidth: 280,
      p: 0,
    },
  },
  arrow: { sx: { color: "background.paper", "&::before": { border: 1, borderColor: "divider" } } },
} as const;

const TransitionArrowTooltip: React.FC<{
  currentSegment: AssignmentTimelineSegment;
  nextSegment: AssignmentTimelineSegment;
  auditLog: AuditLog;
  open: boolean;
}> = ({ currentSegment, nextSegment, auditLog, open }) => {
  const isReassignment = currentSegment.kind === "assigned" && nextSegment.kind === "assigned";

  if (isReassignment) {
    const unassignLog = currentSegment.unassignAuditLog;
    const assignLog = nextSegment.assignAuditLog;

    return (
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, mb: 1 }}>
          <SwapHorizIcon sx={{ color: "info.main", fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>Reassigned</Typography>
        </Box>

        {currentSegment.entityType && currentSegment.entityId && (
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.5, mb: 0.25 }}>
            <Typography color="error.main" sx={{ fontSize: "0.7rem", fontWeight: 600, flexShrink: 0 }}>From</Typography>
            <AssignmentEntityLabel
              entityType={currentSegment.entityType}
              entityId={currentSegment.entityId}
              open={open}
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            />
          </Box>
        )}
        {nextSegment.entityType && nextSegment.entityId && (
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.5, mb: 0.75 }}>
            <Typography color="success.main" sx={{ fontSize: "0.7rem", fontWeight: 600, flexShrink: 0 }}>To</Typography>
            <AssignmentEntityLabel
              entityType={nextSegment.entityType}
              entityId={nextSegment.entityId}
              open={open}
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            />
          </Box>
        )}

        {unassignLog && (
          <InfoRow
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label="When"
            value={formatPopoverDateTime(unassignLog.createdAt)}
          />
        )}
        <InfoRow
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          label="By"
          value={getAuditActorLabel(assignLog ?? auditLog)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ mb: 0.75 }}>
        <AuditActionChip action={auditLog.action} />
      </Box>
      <InfoRow
        icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
        label="When"
        value={formatPopoverDateTime(auditLog.createdAt)}
      />
      <InfoRow
        icon={<PersonIcon sx={{ fontSize: 14 }} />}
        label="By"
        value={getAuditActorLabel(auditLog)}
      />
    </Box>
  );
};

const TransitionArrow: React.FC<{
  date: string;
  auditLog: AuditLog;
  currentSegment: AssignmentTimelineSegment;
  nextSegment: AssignmentTimelineSegment;
  open: boolean;
}> = ({ date, auditLog, currentSegment, nextSegment, open }) => {
  const isReassignment = currentSegment.kind === "assigned" && nextSegment.kind === "assigned";
  const isPositive = nextSegment.kind === "assigned";
  const arrowColor = isReassignment ? "info.main" : isPositive ? "success.main" : "error.main";

  return (
    <Tooltip
      title={
        <TransitionArrowTooltip
          currentSegment={currentSegment}
          nextSegment={nextSegment}
          auditLog={auditLog}
          open={open}
        />
      }
      arrow
      enterDelay={200}
      leaveDelay={100}
      placement="bottom"
      slotProps={TOOLTIP_SLOT_PROPS}
    >
      <Box
        sx={{
          alignItems: "center",
          cursor: "default",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          gap: 0.25,
          justifyContent: "center",
          px: 1,
          width: TRANSITION_WIDTH,
        }}
      >
        <Typography color="text.secondary" noWrap sx={{ fontSize: "0.6rem", fontWeight: 600, lineHeight: 1.2, maxWidth: "100%" }}>
          {formatTimelineDateTimeLabel(date)}
        </Typography>
        <Box sx={{ alignItems: "center", display: "flex", width: "100%" }}>
          <Box sx={{ bgcolor: arrowColor, borderRadius: 1, flex: 1, height: 3, opacity: 0.7 }} />
          {isReassignment ? (
            <SwapHorizIcon sx={{ color: arrowColor, fontSize: 18, ml: -0.25 }} />
          ) : (
            <ArrowForwardIcon sx={{ color: arrowColor, fontSize: 18, ml: -0.25, opacity: 0.8 }} />
          )}
        </Box>
        <Typography
          align="center"
          color="text.secondary"
          noWrap
          sx={{ fontSize: "0.55rem", lineHeight: 1.2, maxWidth: "100%" }}
        >
          {getAuditActorLabel(auditLog)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const StateBox: React.FC<{
  segment: AssignmentTimelineSegment;
  tab: DriverHistoryTab;
  open: boolean;
}> = ({ segment, tab, open }) => {
  const isCurrent = segment.kind === "assigned" && !segment.endAt;

  const bgColor = isCurrent
    ? "rgba(46, 125, 50, 0.12)"
    : segment.kind === "assigned"
      ? "primary.main"
      : "grey.500";

  const borderColor = isCurrent
    ? "success.main"
    : segment.kind === "assigned"
      ? "primary.dark"
      : "grey.600";

  const textColor = isCurrent ? "success.dark" : "#fff";
  const iconColor = isCurrent ? "success.main" : "#fff";

  return (
    <Tooltip
      title={<StateBoxPopover segment={segment} open={open} />}
      arrow
      enterDelay={300}
      leaveDelay={150}
      placement="top"
      slotProps={TOOLTIP_SLOT_PROPS}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: bgColor,
          border: 2,
          borderColor,
          borderRadius: 1,
          color: "common.white",
          cursor: "default",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          height: STATE_BOX_HEIGHT,
          justifyContent: "center",
          mx: "auto",
          px: 1,
          py: 0.75,
          textAlign: "center",
          width: SEGMENT_WIDTH - 8,
          "&:hover": { boxShadow: 3, filter: isCurrent ? "brightness(0.96)" : "brightness(1.08)" },
        }}
      >
        <Box sx={{ color: iconColor, display: "flex", opacity: 0.9 }}>
          {segment.kind === "assigned" ? TAB_ICONS[tab] : <BlockIcon sx={{ fontSize: 20 }} />}
        </Box>
        {segment.kind === "assigned" && segment.entityType && segment.entityId ? (
          <AssignmentEntityLabel
            entityType={segment.entityType}
            entityId={segment.entityId}
            open={open}
            sx={{
              "&&": { color: textColor },
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          />
        ) : (
          <Typography sx={{ "&&": { color: textColor }, fontSize: "0.75rem", fontWeight: 700 }}>
            Unassigned
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

interface LaneData {
  tab: DriverHistoryTab;
  label: string;
  icon: React.ReactElement;
  segments: AssignmentTimelineSegment[];
  laneWidth: number;
}

const TimelineLane: React.FC<{
  lane: LaneData;
  open: boolean;
}> = ({ lane, open }) => (
  <Box sx={{ display: "flex", alignItems: "center", minHeight: LANE_HEIGHT }}>
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        gap: 0.5,
        justifyContent: "center",
        width: LANE_LABEL_WIDTH,
      }}
    >
      <Box sx={{ color: "primary.main", display: "flex" }}>{lane.icon}</Box>
      <Typography
        color="text.secondary"
        sx={{ fontSize: "0.7rem", fontWeight: 700, textAlign: "center" }}
      >
        {lane.label}
      </Typography>
    </Box>

    <Box
      sx={{
        alignItems: "center",
        borderLeft: 2,
        borderColor: "divider",
        display: "flex",
        minHeight: LANE_HEIGHT,
        pl: 1.5,
      }}
    >
      {lane.segments.length === 0 ? (
        <Typography color="text.disabled" sx={{ fontSize: "0.75rem", fontStyle: "italic", px: 2 }}>
          No history
        </Typography>
      ) : (
        lane.segments.map((segment, index) => (
          <React.Fragment key={segment.id}>
            <Box sx={{ flexShrink: 0, width: SEGMENT_WIDTH }}>
              <StateBox
                segment={segment}
                tab={lane.tab}
                open={open}
              />
            </Box>
            {index < lane.segments.length - 1 && (
              <TransitionArrow
                date={segment.endAt ?? lane.segments[index + 1].startAt}
                auditLog={getTransitionAuditLog(segment, lane.segments[index + 1])}
                currentSegment={segment}
                nextSegment={lane.segments[index + 1]}
                open={open}
              />
            )}
          </React.Fragment>
        ))
      )}
    </Box>
  </Box>
);

const DriverAssignmentTimeline: React.FC<DriverAssignmentTimelineProps> = ({
  auditLogs,
  visibleTabs,
  isLoading = false,
  open = true,
  topLeftOverlay,
  topRightOverlay,
}) => {
  const allTabConfigs = getDriverHistoryTabConfigs();

  const lanes = useMemo<LaneData[]>(() => {
    return visibleTabs.map((tab) => {
      const config = allTabConfigs.find((item) => item.value === tab);
      const segments = buildAssignmentTimelineSegments(auditLogs, tab);
      const segmentsTotal = segments.length * SEGMENT_WIDTH;
      const transitionsTotal = Math.max(segments.length - 1, 0) * TRANSITION_WIDTH;
      return {
        tab,
        label: config?.label ?? tab,
        icon: config?.icon ?? <LocalShippingIcon fontSize="small" />,
        segments,
        laneWidth: segmentsTotal + transitionsTotal,
      };
    });
  }, [allTabConfigs, auditLogs, visibleTabs]);

  const contentWidth = useMemo(() => {
    const maxLaneWidth = Math.max(...lanes.map((lane) => lane.laneWidth), 0);
    return LANE_LABEL_WIDTH + 12 + Math.max(maxLaneWidth, 300) + 48;
  }, [lanes]);

  const contentHeight = useMemo(() => {
    return lanes.length * LANE_HEIGHT + Math.max(lanes.length - 1, 0) * LANE_GAP;
  }, [lanes.length]);

  if (isLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!visibleTabs.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary" sx={{ fontSize: "0.875rem" }}>
          Toggle at least one asset type to see the timeline.
        </Typography>
      </Box>
    );
  }

  return (
    <PanZoomViewport
      contentHeight={contentHeight}
      contentWidth={contentWidth}
      fitToContent
      height="100%"
      topLeftOverlay={topLeftOverlay}
      topRightOverlay={topRightOverlay}
    >
      <Box sx={{ height: contentHeight, position: "relative", width: contentWidth }}>
        {lanes.map((lane, index) => (
          <Box
            key={lane.tab}
            sx={{
              position: "absolute",
              top: index * (LANE_HEIGHT + LANE_GAP),
              left: 0,
              width: contentWidth,
            }}
          >
            <TimelineLane
              lane={lane}
              open={open}
            />
          </Box>
        ))}
      </Box>
    </PanZoomViewport>
  );
};

export default DriverAssignmentTimeline;
